// ReportHotelSummaryTable.jsx
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    TableSortLabel,
    Tooltip,
    Typography,
} from "@mui/material";

import HoverPaper from "../shared/HoverPaper";
import NoDataRow from "../NoDataRow";
import { fns } from "../../helpers/common";
import {
    COMPARISON_FILTER,
    DEFAULT_COMPARISON,
    DISPLAY_ELEMENT_HELPER,
    GROUP_SITE_ID,
    getMetricName,
} from "../../helpers/filters";
import SmallPercentage from "../SmallPercentage";
import TooltipComparison from "../shared/TooltipComparison";
import {
    getHotelName,
    HOTELS_INFO,
    SanitizeHotelID,
} from "../../configuration";
import InfoIcon from "../InfoIcon";
import { selectHotels } from "../../features/hotels/hotelsApi";

const getAggregatedMetric = (rows, source, metric) => {
    const total = (name) =>
        rows.reduce((sum, row) => sum + (Number(row[source]?.[name]) || 0), 0);

    if (metric === "ADRTotal")
        return total("roomNightsTotal")
            ? total("revenueRoom") / total("roomNightsTotal")
            : 0;

    if (metric === "ALoSTotal")
        return total("bookingsRooms")
            ? total("roomNightsTotal") / total("bookingsRooms")
            : 0;

    if (metric === "ABWTotal")
        return total("bookingsRooms")
            ? Math.round(total("BWTotal") / total("bookingsRooms"))
            : 0;

    if (metric === "cancellationRate")
        return total("bookingsTotal")
            ? (total("bookingsCancelled") / total("bookingsTotal")) * 100
            : 0;

    return total(metric);
};

export const REPORT_HOTEL_SUMMARY_TABLE = "REPORT_HOTEL_SUMMARY_TABLE";
const ReportHotelSummaryTable = ({
    data = {},
    js = {},
    title = "Hotel summary",
    description,
    metrics = [
        "revenueRoom",
        "bookingsRooms",
        "roomNightsTotal",
        "ADRTotal",
        "ALoSTotal",
        "ABWTotal",
        "cancellationRate",
        "revenueVouchers",
    ],
    defaultMetricSort = "revenueRoom",
    comparisonMetrics = [
        "revenueRoom",
        "bookingsRooms",
        "roomNightsTotal",
        "ADRTotal",
        "ALoSTotal",
        "ABWTotal",
        "cancellationRate",
        "revenueVouchers",
    ],
    comparisonText = "comparison",
    place = "default",
    selectedHotels = [],
}) => {
    // console.log({ data, title, REPORT_HOTEL_SUMMARY_TABLE });
    const { id: hotelID } = useSelector((state) => state.hotelID);
    const [sortMetric, setSortMetric] = useState(defaultMetricSort);
    const [sortDirection, setSortDirection] = useState("desc");

    const handleSort = (metric) => {
        setSortDirection(
            sortMetric === metric && sortDirection === "desc" ? "asc" : "desc",
        );
        setSortMetric(metric);
    };

    const comparisonLabel =
        COMPARISON_FILTER[comparisonText]?.label ?? comparisonText;

    const { data: hotels = [] } = useSelector(selectHotels);

    const dataRows = useMemo(
        () =>
            Object.values(data ?? {}).map(
                ({ keys, result, resultOneYearBefore }) => ({
                    siteID:
                        Object.fromEntries(keys ?? [])[GROUP_SITE_ID] ?? null,
                    result: result ?? {},
                    resultOneYearBefore: resultOneYearBefore ?? {},
                }),
            ),
        [data, hotels],
    );
    const rows = useMemo(() => {
        return (
            selectedHotels.length
                ? selectedHotels.map((selectedHotel) => {
                      const selectedHotelID = SanitizeHotelID(selectedHotel);
                      const hotelInfo = HOTELS_INFO[selectedHotelID];

                      const dataHotelName = getHotelName(selectedHotel, hotels);

                      const dataHotel = hotels.find(
                          ({ HotelID }) =>
                              String(HotelID) === String(selectedHotelID),
                      );

                      const siteIDs = new Set(
                          [
                              ...String(dataHotel?.SiteIDs ?? "").split(","),
                              ...(dataHotel?.Sites ?? []).map(
                                  ({ SiteID }) => SiteID,
                              ),
                          ]
                              .map((siteID) => String(siteID ?? "").trim())
                              .filter(Boolean),
                      );

                      const sourceRows =
                          selectedHotels.length === 1 &&
                          dataRows.length === 1 &&
                          dataRows[0].siteID == null
                              ? dataRows
                              : dataRows.filter(({ siteID }) =>
                                    siteIDs.has(String(siteID)),
                                );

                      //   console.log({
                      //       selectedHotel,
                      //       selectedHotelID,
                      //       dataHotel,
                      //       siteIDs: [...siteIDs],
                      //       dataRowSiteIDs: dataRows.map(({ siteID }) => siteID),
                      //       sourceRows,
                      //   });

                      const row = {
                          hotel:
                              getHotelName(selectedHotel, hotels, false) ??
                              dataHotel?.HotelName ??
                              dataHotelName ??
                              "Unknown",
                          isLead: Boolean(hotelInfo?.parent),
                          groupHotels: hotelInfo?.parent
                              ? [
                                    ...new Set(
                                        (dataHotel?.Sites ?? [])
                                            .map(({ SiteName }) => SiteName)
                                            .filter(Boolean),
                                    ),
                                ].sort()
                              : [],
                      };

                      metrics.forEach((metric) => {
                          const current = getAggregatedMetric(
                              sourceRows,
                              "result",
                              metric,
                          );
                          const previous = getAggregatedMetric(
                              sourceRows,
                              "resultOneYearBefore",
                              metric,
                          );

                          row[metric] = current;
                          row[`${metric}Comparison`] = previous;
                          row[`${metric}Diff`] = current - previous;
                          row[`${metric}DiffPercent`] = previous
                              ? ((current - previous) / Math.abs(previous)) *
                                100
                              : null;
                      });

                      return row;
                  })
                : dataRows.map((dataRow) => {
                      const row = { hotel: dataRow.hotel };

                      metrics.forEach((metric) => {
                          const current = getAggregatedMetric(
                              [dataRow],
                              "result",
                              metric,
                          );
                          const previous = getAggregatedMetric(
                              [dataRow],
                              "resultOneYearBefore",
                              metric,
                          );

                          row[metric] = current;
                          row[`${metric}Comparison`] = previous;
                          row[`${metric}Diff`] = current - previous;
                          row[`${metric}DiffPercent`] = previous
                              ? ((current - previous) / Math.abs(previous)) *
                                100
                              : null;
                      });

                      return row;
                  })
        )
            .filter((row) =>
                metrics.some(
                    (metric) =>
                        row[metric] !== 0 || row[`${metric}Comparison`] !== 0,
                ),
            )
            .sort((a, b) => {
                const comparison =
                    sortMetric === "hotel"
                        ? a.hotel.localeCompare(b.hotel)
                        : (Number(a[sortMetric]) || 0) -
                          (Number(b[sortMetric]) || 0);

                return sortDirection === "asc" ? comparison : -comparison;
            });
    }, [dataRows, hotels, metrics, selectedHotels, sortMetric, sortDirection]);
    // console.log({ rows, hotels, dataRows, selectedHotels });
    const totals = useMemo(
        () =>
            Object.fromEntries(
                metrics.map((metric) => [
                    metric,
                    getAggregatedMetric(dataRows, "result", metric),
                ]),
            ),
        [dataRows, metrics],
    );

    const renderComparison = (row, metric) => {
        if (!comparisonMetrics.includes(metric)) return null;

        const current = Number(row[metric]) || 0;
        const previous = Number(row[`${metric}Comparison`]);

        if (isNaN(previous) || previous === 0) return null;

        return (
            <Box sx={{ pl: 1 }}>
                <SmallPercentage
                    brackets={false}
                    tooltip={false}
                    bubble={true}
                    bubbleRound={true}
                    actual={current}
                    old={previous}
                    showFormula={false}
                    isInverted={
                        (DISPLAY_ELEMENT_HELPER[metric] ?? {}).upside ?? false
                    }
                    styles={{
                        fontSize: "11px",
                        justifyContent: "flex-end",
                    }}
                />
            </Box>
        );
    };

    return (
        <>
            <InfoIcon title={title} description={description} />

            <Box component={HoverPaper}>
                <TableContainer sx={{ overflow: "auto" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                    <TableSortLabel
                                        active={sortMetric === "hotel"}
                                        direction={
                                            sortMetric === "hotel"
                                                ? sortDirection
                                                : "asc"
                                        }
                                        onClick={() => handleSort("hotel")}
                                    >
                                        Hotel
                                    </TableSortLabel>
                                </TableCell>

                                {metrics.map((metric) => (
                                    <TableCell
                                        key={metric}
                                        align="right"
                                        sx={{
                                            fontWeight: "bold",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        <TableSortLabel
                                            active={sortMetric === metric}
                                            direction={
                                                sortMetric === metric
                                                    ? sortDirection
                                                    : "desc"
                                            }
                                            onClick={() => handleSort(metric)}
                                        >
                                            {DISPLAY_ELEMENT_HELPER[metric]
                                                ?.abbreviation ??
                                                getMetricName(metric, place) ??
                                                metric}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {rows.length > 0 ? (
                                rows.map((row) => (
                                    <TableRow key={row.hotel}>
                                        <TableCell
                                            sx={{ whiteSpace: "nowrap" }}
                                        >
                                            <Stack
                                                direction="row"
                                                spacing={0.25}
                                                alignItems="center"
                                            >
                                                <span>{row.hotel}</span>

                                                {row.isLead && (
                                                    <Tooltip
                                                        arrow
                                                        title={
                                                            <Box>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        mb: 0.5,
                                                                    }}
                                                                >
                                                                    This is a
                                                                    hotel group.
                                                                    The
                                                                    displayed
                                                                    values
                                                                    combine data
                                                                    from the
                                                                    following
                                                                    hotels:
                                                                </Typography>

                                                                {row.groupHotels.map(
                                                                    (hotel) => (
                                                                        <Typography
                                                                            key={
                                                                                hotel
                                                                            }
                                                                            variant="body2"
                                                                        >
                                                                            •{" "}
                                                                            {
                                                                                hotel
                                                                            }
                                                                        </Typography>
                                                                    ),
                                                                )}

                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        mt: 0.5,
                                                                    }}
                                                                >
                                                                    Values
                                                                    registered
                                                                    directly
                                                                    against the
                                                                    group may
                                                                    also be
                                                                    included.
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    >
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                cursor: "help",
                                                                fontWeight:
                                                                    "bold",
                                                            }}
                                                        >
                                                            *
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>

                                        {metrics.map((metric) => (
                                            <TableCell
                                                key={metric}
                                                align="right"
                                                sx={{ whiteSpace: "nowrap" }}
                                            >
                                                <TooltipComparison
                                                    metricName={metric}
                                                    comparisonType={
                                                        js?.filters
                                                            ?.Comparison ??
                                                        DEFAULT_COMPARISON
                                                    }
                                                    comparisonCustom={
                                                        js?.filters
                                                            ?.ComparisonCustom
                                                    }
                                                    fromDay={js?.fromDay}
                                                    toDay={js?.toDay}
                                                    value={row[metric] ?? 0}
                                                    valueOld={
                                                        row[
                                                            `${metric}Comparison`
                                                        ]
                                                    }
                                                    dataType={js?.dataType}
                                                    show={comparisonMetrics.includes(
                                                        metric,
                                                    )}
                                                    valueFormat={
                                                        DISPLAY_ELEMENT_HELPER[
                                                            metric
                                                        ]?.format ?? {}
                                                    }
                                                >
                                                    <Stack
                                                        direction="row"
                                                        spacing={0.75}
                                                        justifyContent="flex-end"
                                                        alignItems="center"
                                                    >
                                                        {fns(
                                                            hotelID,
                                                            row[metric] ?? 0,
                                                            DISPLAY_ELEMENT_HELPER[
                                                                metric
                                                            ]?.format ?? {},
                                                        )}
                                                        {renderComparison(
                                                            row,
                                                            metric,
                                                        )}
                                                    </Stack>
                                                </TooltipComparison>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                <NoDataRow text="No data for these filters" />
                            )}
                        </TableBody>
                        {rows.length > 0 && (
                            <TableFooter>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            fontWeight: "bold",
                                            borderTop: "2px solid",
                                            borderColor: "divider",
                                        }}
                                    >
                                        Total
                                    </TableCell>

                                    {metrics.map((metric) => (
                                        <TableCell
                                            key={metric}
                                            align="right"
                                            sx={{
                                                fontWeight: "bold",
                                                whiteSpace: "nowrap",
                                                borderTop: "2px solid",
                                                borderColor: "divider",
                                            }}
                                        >
                                            {fns(
                                                hotelID,
                                                totals[metric],
                                                DISPLAY_ELEMENT_HELPER[metric]
                                                    ?.format ?? {},
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableFooter>
                        )}
                    </Table>
                </TableContainer>
            </Box>
        </>
    );
};

export default ReportHotelSummaryTable;
